import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Exercise,
  CreateExercise,
  ExerciseHistory,
} from 'src/models/exercise.model';
import { ExerciseService } from 'src/services/exercise-service.service';
import { DbExercisesService } from 'src/services/db-exercises.service';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { DbExercise } from 'src/models/db-exercises.model';

@Component({
    selector: 'app-exercise-detail',
    templateUrl: './exercise-detail.component.html',
    styleUrls: ['./exercise-detail.component.scss'],
    standalone: false
})
export class ExerciseDetailComponent implements OnInit {
  detail!: Exercise;
  exerciseHistories: ExerciseHistory[] = [];
  dbExercise!: DbExercise;
  editMode = false;
  loading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private exerciseService: ExerciseService,
    private dbExerciseService: DbExercisesService,
    private auth: Auth,
    private location: Location
  ) {}

  async ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.editMode = this.route.snapshot.queryParamMap.get('edit') === 'true';

    if (id) {
      const detail = await this.exerciseService.getExerciseById(id);
      this.detail = detail!;

      // Load catalogue metadata independently of workout-history auth/query
      // timing. A delayed history request must not leave this detail page
      // without its image, description and instructions.
      if (this.detail.dbExerciseId) {
        // This page can be opened directly. In that case the in-memory DB
        // exercise catalogue has not been populated by the preview page yet.
        this.dbExercise = this.dbExerciseService.getExerciseById(
          this.detail.dbExerciseId
        )!;
        if (!this.dbExercise) {
          await this.dbExerciseService.fetchAndDecompressData();
          this.dbExercise = this.dbExerciseService.getExerciseById(
            this.detail.dbExerciseId
          )!;
        }
      }

      this.exerciseHistories =
        await this.exerciseService.getExerciseHistoriesByExerciseId(id);
    }
  }

  goBack() {
    this.location.back();
  }

  close() {
    this.router.navigate(['/add-sport']);
  }

  async changeExercise(data: CreateExercise) {
    this.loading = true;

    const user = await firstValueFrom(authState(this.auth));
    if (user) {
      let imageUrl = '';
      if (data.selectedFile) {
        imageUrl = await this.exerciseService.uploadImage(
          data.selectedFile,
          user.uid
        );
      } else if (
        data.imagePreview &&
        typeof data.imagePreview === 'string' &&
        data.imagePreview.startsWith('http')
      ) {
        imageUrl = data.imagePreview;
      } else if (data.imagePreview && typeof data.imagePreview === 'string') {
        imageUrl = await this.exerciseService.uploadImageFromUrl(
          data.imagePreview,
          user.uid
        );
      }

      imageUrl = imageUrl || '';

      const selectedHashtags = Array.from(data.toggleStates.entries())
        .filter(([group, toggled]) => toggled)
        .map(([group]) => group);

      const selectedSecondaryHashtags = Array.from(
        data.secondaryToggleStates.entries()
      )
        .filter(([group, toggled]) => toggled)
        .map(([group]) => group);

      const updatedExercise: Exercise = {
        userId: user.uid,
        sportName: data.sportName,
        description: data.description,
        hashtags: selectedHashtags,
        secondaryHashtags: selectedSecondaryHashtags,
        imageUrl: imageUrl,
      };

      await this.exerciseService.updateExercise(
        this.detail.id!,
        updatedExercise
      );

      console.log('Exercise updated successfully');
      this.loading = false;
      this.editMode = false;
      this.detail = { ...this.detail, ...updatedExercise };
    }
  }

  removeHistory(history: ExerciseHistory) {
    this.exerciseService.removeExerciseHistory(history.id!);
    this.exerciseHistories = this.exerciseHistories.filter(
      (exerciseHistory) => exerciseHistory.id !== history.id
    );
  }
}
